import React, { useState } from 'react';
import StatsCard from './StatsCard';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Paper, Typography, Box, FormControl, InputLabel, Select, MenuItem, Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import ModalOmni from 'allGlobalComponents/ModalOmni';

const GROUP_BY_OPTIONS = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' }
];

const useStyles = makeStyles((theme) => ({
    insightsSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(3),
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    insightsTitle: {
        fontSize: '2rem',
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary,
    },
    headerSection: {
        display: 'flex',
        alignItems: "center",
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing(2),
    },
    filterContainer: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5)
    },
    buttonContainer: {
        width: "100%",
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary
    }
}));

function StatsGrid({ loading, stats, groupBy, onGroupByChange, onRefresh }) {
    const classes = useStyles();
    const theme = useTheme();
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [tempGroupBy, setTempGroupBy] = useState(groupBy);

    const {
        totalMessagesSent = 0,
        responseRate = 0,
        avgResponseTime = '0h 0m',
        customerEngagementRate = 0,
        uniqueCustomers = 0,
        customersResponded = 0,
        noResponseRate = 0
    } = stats;

    // Calculate active filter count
    const getActiveFilterCount = () => {
        let count = 0;
        if (groupBy !== 'week') count++; // 'week' is the default
        return count;
    };

    const calculatedFilterCount = getActiveFilterCount();

    // Apply filters
    const handleApplyFilters = () => {
        onGroupByChange(tempGroupBy);
        setFilterDialogOpen(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        setTempGroupBy('week');
        onGroupByChange('week');
        setFilterDialogOpen(false);
    };

    // Cancel filter changes
    const handleCancelFilters = () => {
        setTempGroupBy(groupBy);
        setFilterDialogOpen(false);
    };

    // Open filter dialog
    const handleOpenFilterDialog = () => {
        setTempGroupBy(groupBy);
        setFilterDialogOpen(true);
    };

    // Filter dialog content
    const filterDialogContent = (
        <div className={classes.filterContainer}>
            <div>
                <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Group By</InputLabel>
                    <Select
                        value={tempGroupBy}
                        onChange={(e) => setTempGroupBy(e.target.value)}
                        label="Group By"
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    backgroundColor: theme.palette.background.default,
                                    color: theme.palette.text.primary
                                }
                            }
                        }}
                    >
                        {GROUP_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        </div>
    );

    // Filter dialog actions
    const filterDialogActions = (
        <div className={classes.buttonContainer}>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleClearFilters} size="small" variant="outlined" label="Clear" />
            </div>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleCancelFilters} size="small" variant="outlined" label="Cancel" />
                <ButtonOmni onClick={handleApplyFilters} size="small" variant="outlined" label="Apply" />
            </div>
        </div>
    );

    return (
        <>
            <Box className={classes.insightsSection}>
                <Box className={classes.headerSection}>
                    <Typography className={classes.insightsTitle}>
                        Summary
                    </Typography>
                    {calculatedFilterCount > 0 ? (
                        <Badge badgeContent={calculatedFilterCount} color="primary">
                            <ButtonOmni
                                loading={false}
                                onClick={handleOpenFilterDialog}
                                size="small"
                                variant="outlined"
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <FilterListIcon />
                                        Filters
                                    </Box>
                                }
                            />
                        </Badge>
                    ) : (
                        <ButtonOmni
                            loading={false}
                            onClick={handleOpenFilterDialog}
                            size="small"
                            variant="outlined"
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FilterListIcon />
                                    Filters
                                </Box>
                            }
                        />
                    )}
                </Box>

                <div className={classes.statsGrid}>
                    <StatsCard
                        title="Total Messages Sent"
                        value={totalMessagesSent.toLocaleString()}
                        loading={loading}
                    />
                    <StatsCard
                        title="Response Rate"
                        value={`${responseRate}%`}
                        loading={loading}
                    />
                    <StatsCard
                        title="No Response Rate"
                        value={`${noResponseRate}%`}
                        loading={loading}
                    />
                    <StatsCard
                        title="Avg Response Time"
                        value={avgResponseTime}
                        loading={loading}
                    />
                    <StatsCard
                        title="Customer Engagement"
                        value={`${customerEngagementRate}%`}
                        loading={loading}
                    />
                    <StatsCard
                        title="Unique Customers"
                        value={uniqueCustomers.toLocaleString()}
                        loading={loading}
                    />
                    <StatsCard
                        title="Customers Responded"
                        value={customersResponded.toLocaleString()}
                        loading={loading}
                    />
                </div>
            </Box>

            <ModalOmni
                open={filterDialogOpen}
                handleClose={() => setFilterDialogOpen(false)}
                title="Stats & Charts Filters"
                content={filterDialogContent}
                actions={filterDialogActions}
                customWidth={600}
            />
        </>
    );
}

export default StatsGrid;